/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import { Walker, isIdentifier } from '@kbn/esql-ast';
import { FunctionDefinitionTypes } from '../definitions/types';
import { EDITOR_MARKER } from './constants';
import { isColumnItem, isSourceItem, pipePrecedesCurrentWord, getFunctionDefinition, isOptionItem } from './helpers';
function findNode(nodes, offset) {
  for (const node of nodes) {
    if (Array.isArray(node)) {
      const ret = findNode(node, offset);
      if (ret) {
        return ret;
      }
    } else {
      if (node && node.location && node.location.min <= offset && node.location.max >= offset) {
        if ('args' in node) {
          const ret = findNode(node.args, offset);
          // if the found node is the marker, then return its parent
          if ((ret === null || ret === void 0 ? void 0 : ret.text) === EDITOR_MARKER) {
            return node;
          }
          if (ret) {
            return ret;
          }
        }
        return node;
      }
    }
  }
}
function findCommand(ast, offset) {
  const commandIndex = ast.findIndex(({
    location
  }) => location.min <= offset && location.max >= offset);
  return ast[commandIndex] || ast[ast.length - 1];
}
function findOption(nodes, offset) {
  return findCommandSubType(nodes, offset, isOptionItem);
}
function findCommandSubType(nodes, offset, isOfTypeFn) {
  for (const node of nodes) {
    if (isOfTypeFn(node)) {
      if (node.location.min <= offset && node.location.max >= offset || nodes[nodes.length - 1] === node && node.location.max < offset) {
        return node;
      }
    }
  }
}
export function isMarkerNode(node) {
  if (Array.isArray(node)) {
    return false;
  }
  return Boolean(node && (isColumnItem(node) || isIdentifier(node) || isSourceItem(node)) && node.name.endsWith(EDITOR_MARKER));
}
function cleanMarkerNode(node) {
  return isMarkerNode(node) ? undefined : node;
}
function isNotMarkerNodeOrArray(arg) {
  return Array.isArray(arg) || !isMarkerNode(arg);
}
function mapToNonMarkerNode(arg) {
  return Array.isArray(arg) ? arg.filter(isNotMarkerNodeOrArray).map(mapToNonMarkerNode) : arg;
}
export function removeMarkerArgFromArgsList(node) {
  if (!node) {
    return;
  }
  if (node.type === 'command' || node.type === 'option' || node.type === 'function') {
    return {
      ...node,
      args: node.args.filter(isNotMarkerNodeOrArray).map(mapToNonMarkerNode)
    };
  }
  return node;
}
function findAstPosition(ast, offset) {
  const command = findCommand(ast, offset);
  if (!command) {
    return {
      command: undefined,
      node: undefined
    };
  }
  const containingFunction = Walker.findAll(command, node => {
    var _node$location, _node$location2;
    return node.type === 'function' && node.subtype === 'variadic-call' && ((_node$location = node.location) === null || _node$location === void 0 ? void 0 : _node$location.min) <= offset && ((_node$location2 = node.location) === null || _node$location2 === void 0 ? void 0 : _node$location2.max) >= offset;
  }).pop();
  return {
    command: removeMarkerArgFromArgsList(command),
    containingFunction: removeMarkerArgFromArgsList(containingFunction),
    option: removeMarkerArgFromArgsList(findOption(command.args, offset)),
    node: removeMarkerArgFromArgsList(cleanMarkerNode(findNode(command.args, offset)))
  };
}
function isNotEnrichClauseAssigment(node, command) {
  return node.name !== '=' && command.name !== 'enrich';
}
function isOperator(node) {
  var _getFunctionDefinitio;
  return ((_getFunctionDefinitio = getFunctionDefinition(node.name)) === null || _getFunctionDefinitio === void 0 ? void 0 : _getFunctionDefinitio.type) === FunctionDefinitionTypes.OPERATOR;
}

/**
 * Given a ES|QL query string, its AST and the cursor position,
 * it returns the type of context for the position ("list", "function", "option", "setting", "expression", "newCommand")
 * plus the whole hierarchy of nodes (command, option, setting and actual position node) context.
 *
 * Type details:
 * * "list": the cursor is inside a "in" list of values (i.e. `a in (1, 2, <here>)`)
 * * "function": the cursor is inside a function call (i.e. `fn(<here>)`)
 * * "expression": the cursor is inside a command expression (i.e. `command ... <here>` or `command a = ... <here>`)
 * * "newCommand": the cursor is at the beginning of a new command (i.e. `command1 | command2 | <here>`)
 */
export function getAstContext(queryString, ast, offset) {
  let inComment = false;
  Walker.visitComments(ast, node => {
    if (node.location && node.location.min <= offset && node.location.max >= offset) {
      inComment = true;
    }
  });
  if (inComment) {
    return {
      type: 'comment'
    };
  }
  let withinStatsWhereClause = false;
  Walker.walk(ast, {
    visitFunction: fn => {
      if (fn.name === 'where' && fn.location.min <= offset) {
        withinStatsWhereClause = true;
      }
    }
  });
  const {
    command,
    option,
    node,
    containingFunction
  } = findAstPosition(ast, offset);
  if (node) {
    if (node.type === 'literal' && node.literalType === 'keyword') {
      // command ... "<here>"
      return {
        type: 'value',
        command,
        node,
        option,
        containingFunction
      };
    }
    if (node.type === 'function') {
      if (['in', 'not_in'].includes(node.name) && Array.isArray(node.args[1])) {
        // command ... a in ( <here> )
        return {
          type: 'list',
          command,
          node,
          option,
          containingFunction
        };
      }
      if (isNotEnrichClauseAssigment(node, command) && (!isOperator(node) || command.name === 'stats' && !withinStatsWhereClause)) {
        // command ... fn( <here> )
        return {
          type: 'function',
          command,
          node,
          option,
          containingFunction
        };
      }
    }
  }
  if (!command || queryString.length <= offset && pipePrecedesCurrentWord(queryString)) {
    //   // ... | <here>
    return {
      type: 'newCommand',
      command: undefined,
      node,
      option,
      containingFunction
    };
  }

  // command a ... <here> OR command a = ... <here>
  return {
    type: 'expression',
    command,
    containingFunction,
    option,
    node
  };
}